import React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap,
  Heart,
  Award,
  MapPin,
  Brain,
  Sparkles,
  Focus,
  Zap,
  User,
  Smile,
  ArrowRight,
  CheckCircle2,
  Shirt,
  Clock,
  Building2,
  ChevronDown,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  heroContent,
  trustBadges,
  hrTrustBadges,
  ammaAssisSummary,
  benefits,
  interventionSteps,
  faqItems,
  images,
} from '../data/mock';

const iconMap = {
  GraduationCap,
  Heart,
  Award,
  MapPin,
  Brain,
  Sparkles,
  Focus,
  Zap,
  User,
  Smile,
  Shirt,
  Clock,
  Building2,
};

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={images.hero}
            alt="Chaise Amma assis professionnelle"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="max-w-2xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium text-foreground leading-tight mb-2 animate-fade-in-up">
              {heroContent.title}
            </h1>
            <p className="font-heading text-2xl md:text-3xl text-primary font-normal mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              {heroContent.titleHighlight}
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-xl animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              {heroContent.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <Link to={heroContent.ctaPrimaryLink} className="btn-primary">
                {heroContent.ctaPrimary}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to={heroContent.ctaSecondaryLink} className="btn-primary bg-primary text-white hover:bg-primary/90">
                {heroContent.ctaSecondary}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HR Trust Badges Section */}
      <section className="py-12 bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {hrTrustBadges.map((badge) => {
              const Icon = iconMap[badge.icon];
              return (
                <div
                  key={badge.id}
                  className="flex items-center gap-4 p-5 bg-secondary/30 border border-border/50 hover-lift"
                >
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-medium text-foreground">
                      {badge.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {badge.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustBadges.map((badge) => {
              const Icon = iconMap[badge.icon];
              return (
                <div
                  key={badge.id}
                  className="flex flex-col items-center text-center p-6 hover-lift"
                >
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-medium text-foreground mb-1">
                    {badge.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {badge.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Amma Assis Summary Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-6">
                {ammaAssisSummary.title}
              </h2>
              <p className="text-muted-foreground leading-relaxed text-lg mb-8">
                {ammaAssisSummary.description}
              </p>
              <Link
                to={ammaAssisSummary.link}
                className="btn-secondary inline-flex items-center gap-2"
              >
                {ammaAssisSummary.linkText}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative">
              <img
                src={images.ammaAssis}
                alt="Séance de Amma assis"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Intervention Steps Section - NEW */}
      <section className="py-24 section-gradient">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-4">
              Déroulé d'une intervention en entreprise
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Une organisation simple et rapide pour offrir un moment de détente à vos équipes.
            </p>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            {interventionSteps.map((step, index) => (
              <div
                key={step.id}
                className="relative bg-white p-6 border border-border card-hover"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {step.id}
                  </div>
                  {step.duration && (
                    <span className="text-xs text-primary font-medium bg-primary/10 px-2 py-1">
                      {step.duration}
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-lg font-medium text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {step.description}
                </p>
                {index < interventionSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-4 h-4 text-primary/40" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-4">
              Bienfaits pour les salariés
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Le Amma assis offre des bénéfices immédiats et durables pour le bien-être au travail.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
            {benefits.map((benefit) => {
              const Icon = iconMap[benefit.icon];
              return (
                <div
                  key={benefit.id}
                  className="bg-white p-8 card-hover border border-border"
                >
                  <div className="w-12 h-12 bg-accent flex items-center justify-center mb-6">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-medium text-foreground mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Break with Corporate Feel */}
      <section className="relative h-[400px]">
        <img
          src={images.entreprises}
          alt="Bien-être en entreprise"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/10" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white bg-foreground/80 p-10 max-w-xl mx-4">
            <p className="font-heading text-2xl md:text-3xl font-medium mb-4">
              "Un investissement simple pour le bien-être de vos équipes"
            </p>
            <p className="text-sm opacity-90">
              Qualité de Vie au Travail • Prévention • Performance
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section - NEW */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-4">
              Questions fréquentes
            </h2>
            <p className="text-muted-foreground">
              Tout ce que vous devez savoir avant de planifier une intervention.
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item) => (
              <AccordionItem
                key={item.id}
                value={`item-${item.id}`}
                className="border border-border bg-white px-6"
              >
                <AccordionTrigger className="text-left font-heading text-lg font-medium text-foreground hover:text-primary py-5">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-primary/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-6">
            Prêt à offrir du bien-être à vos équipes ?
          </h2>
          <p className="text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
            Contactez-moi pour discuter de vos besoins et obtenir un devis personnalisé 
            pour vos interventions en entreprise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary bg-primary text-white hover:bg-primary/90">
              Demander un devis entreprise
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link to="/entreprises" className="btn-primary">
              En savoir plus
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
